import { html } from 'lit-element';
import {
  bubbleStyles,
  choiceValueStyles,
  defaultCellFontSize,
  responseStyles,
} from '../cssUtils.js';
import { getIcon, renderBubble } from '../utils.js';
import { CustomLitElement } from '../CustomLitElement.js';
import {
  defineElement,
  importMathRendering,
} from '../classroom-tool-extensions/classifier/utils.js';
import { PieMathRenderer } from '../classroom-tool-extensions/common/PieMathRenderer.js';

/**
 * @element select-text-response
 * @prop {Object} model - The model for the pie
 * @prop {Object} session - The response session
 * @cssprop --pie-live-session-background-color - change the background color
 * @cssprop --pie-live-session-text-color - change the text color
 * @cssprop --pie-live-session-bubble-text-color - change the text color
 * @cssprop --pie-live-session-font-family - change the font family
 * @cssprop --pie-live-session-font-size-small - change the font size
 * @cssprop --pie-live-session-response-height - change the height (must be valid, eg: 100px, 10em )
 * @cssprop --pie-live-session-response-width - change the width (must be valid, eg: 100px, 10em )
 */
export class SelectTextResponse extends CustomLitElement {
  static get properties() {
    return {
      model: { type: Object, attribute: false },
      session: { type: Array, attribute: false },
      hoveredChoice: { type: Object },
    };
  }

  set hoveredChoice(val) {
    const oldVal = this._hoveredChoice;

    if (!oldVal || !val || oldVal.value !== val.value) {
      this._hoveredChoice = val;
      this.requestUpdate('hoveredChoice', oldVal);
    }
  }

  get hoveredChoice() {
    return this._hoveredChoice;
  }

  constructor() {
    super();
    this._hoveredChoice = null;
  }

  createRenderRoot() {
    return this;
  }

  updated(_changedProperties) {
    super.updated(_changedProperties);

    defineElement('math-rendering-element', PieMathRenderer);

    if (!this.mathRenderingModuleUrlImported) {
      importMathRendering();

      this.mathRenderingModuleUrlImported = true;
    }
  }

  mouseover(e, value) {
    const item = e.target.closest('.choice-value');

    if (item && item.getBoundingClientRect) {
      const bounds = item.getBoundingClientRect();

      this.hoveredChoice = {
        value,
        top: bounds.top + bounds.height,
        left: bounds.left + bounds.width,
      };
    }
  }

  mouseout() {
    this.hoveredChoice = null;
  }

  _renderResponse() {
    const { tokens } = this.model || {};
    const { selectedTokens } = this.session || {};

    if (
      !tokens ||
      !tokens.length ||
      !selectedTokens ||
      !selectedTokens.length
    ) {
      return html``;
    }

    return selectedTokens.map(selectedToken => {
      const token = tokens.find(
        t =>
          (t.start === selectedToken.start && t.end === selectedToken.end) ||
          // this case is used for the cases when the token's start & end were recalculated
          (t.start === selectedToken.oldStart && t.end === selectedToken.oldEnd)
      );

      if (!token) return html``;

      const { text, correct } = token;

      return html` <div
        class="choice-value"
        @mouseover=${e => this.mouseover(e, text)}
        @mouseout=${this.mouseout}
      >
        <p>
          ${getIcon(correct)}
          ${text.length > 12 ? `${text.slice(0, 12)}...` : text}
        </p>
      </div>`;
    });
  }

  render() {
    return html`
      <style>
        ${bubbleStyles}
          ${choiceValueStyles}
          ${responseStyles}
          .select-text.response {
          font-size: var(
            --pie-live-session-font-size-small,
            ${defaultCellFontSize}px
          );
        }

        .select-text p {
          margin: 2px;
        }
      </style>
      <div class="response select-text">
        ${this._renderResponse()} ${renderBubble(this.hoveredChoice)}
      </div>
    `;
  }
}

export default SelectTextResponse;
